/* eslint-disable max-lines */
import type { DSPropTypesSchema, GlobalAttributesT, ValidationMap, XstyledProps } from '@elliemae/ds-props-helpers';
import {
  PropTypes,
  describe,
  getPropsPerSlotPropTypes,
  globalAttributesPropTypes,
  xstyledPropTypes,
} from '@elliemae/ds-props-helpers';
import { type Theme } from '@elliemae/ds-system';
import type { TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import { DSLeftNavigationName, LEFT_NAVIGATION_SLOTS, outOfTheBoxTypes } from './constants/index.js';
import type { ContextProps } from './LeftNavigationContext.js';

type ColorsKey = keyof Theme['colors'];

export declare namespace DSLeftNavigationT {
  export type SlotFunctionArguments = {
    dsLeftnavigationAlertsIcon: () => object;
    dsLeftnavigationAreasContainer: () => object;
    dsLeftnavigationArrowContainer: () => object;
    dsLeftnavigationBodyAreasContainer: () => object;
    dsLeftnavigationBodyHeaderArea: () => object;
    dsLeftnavigationBodyItemsArea: () => object;
    dsLeftnavigationBottomContainer: () => object;
    dsLeftnavigationCollapsedContainer: () => object;
    dsLeftnavigationExceptionsIcon: () => object;
    dsLeftnavigationFooterItem: () => object;
    dsLeftnavigationFooterLabel: () => object;
    dsLeftnavigationFooterMenu: () => object;
    dsLeftnavigationFooterMenuCollapse: () => object;
    dsLeftnavigationFooterSeparator: () => object;
    dsLeftnavigationHeaderArea: () => object;
    dsLeftnavigationItem: () => object;
    dsLeftnavigationItemBorderBottom: () => object;
    dsLeftnavigationItemChevronBack: () => object;
    dsLeftnavigationItemDate: () => object;
    dsLeftnavigationItemLabel: () => object;
    dsLeftnavigationItemSeparator: () => object;
    dsLeftnavigationLeftContainer: () => object;
    dsLeftnavigationLoaderWrapper: () => object;
    dsLeftnavigationMessagesIcon: () => object;
    dsLeftnavigationNotificationsContainer: () => object;
    dsLeftnavigationNotificationsIcon: () => object;
    dsLeftnavigationRightContainer: () => object;
    dsLeftnavigationRoot: () => object;
    dsLeftnavigationSectionContainer: () => object;
    dsLeftnavigationSectionLabel: () => object;
    dsLeftnavigationSectionRightLabel: () => object;
    dsLeftnavigationVerticalSeparator: () => object;
  };
  export type LabelOveflowT = 'wrap' | 'wrapAll' | 'truncate';

  export interface RequiredProps {}

  export interface DefaultProps {
    expanded: boolean;
    expandedWidth: string;
    openedItem: string | null;
    items: GenericItemItemProps[];
    footerLabel: string;
    labelOverflow: LabelOveflowT;
    onSelectedChange: (item: string | null) => void;
    onFocusChange: (item: string | null) => void;
    onFooterExpand: () => void;
    onFooterClose: () => void;
    onItemClick: (item: GenericItemItemProps, e: React.MouseEvent<HTMLElement>) => void;
    loading: boolean;
    isSkeleton: boolean;
    hideFooterPipe: boolean;
  }
  export interface OptionalProps
    extends TypescriptHelpersT.PropsForGlobalOnSlots<typeof DSLeftNavigationName, typeof LEFT_NAVIGATION_SLOTS> {
    FooterLabelComponent?: React.ComponentType<unknown>;
    selectedItem?: string | null;
    selectedParent?: string | null;
    disableDefaultSelection?: boolean;
    withoutBodyShadow?: boolean;
    actionRef?: React.RefObject<{ setFocusedItem: React.Dispatch<React.SetStateAction<string | null>> }>;
    HeaderComponent?: GenericItemItemProps;
    BodyHeaderComponent?: GenericItemItemProps;
  }

  export interface Props
    extends Partial<DefaultProps>,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLElement>, keyof DefaultProps | keyof OptionalProps | keyof XstyledProps>,
      XstyledProps,
      RequiredProps {}
  export interface InternalProps
    extends DefaultProps,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLElement>, keyof DefaultProps | keyof OptionalProps | keyof XstyledProps>,
      XstyledProps,
      RequiredProps {}

  export type OutOfTheBoxT =
    | 'ds-left-nav-item-header'
    | 'ds-left-nav-item-link'
    | 'ds-left-nav-item-submenu'
    | 'ds-left-nav-item-controlled-drilldown'
    | 'ds-left-nav-item-uncontrolled-drilldown'
    | 'ds-left-nav-item-section'
    | 'ds-left-nav-item-separator'
    | 'ds-left-nav-item-text-label'
    | 'ds-left-nav-item-with-date';

  export interface ItemOptsT extends Record<string, unknown> {
    label?: string | JSX.Element;
    labelBold?: boolean;
    labelColor?: [ColorsKey, keyof Theme['colors'][ColorsKey]];
    labelOverflow?: LabelOveflowT;
    labelRightSection?: string | JSX.Element;
    selectable?: boolean;
    focuseable?: boolean;
    startOpened?: boolean;
    isOpened?: boolean;
    openable?: boolean;
    closable?: boolean;
    indent?: number;
    LeftComponent?: React.ComponentType<ComponentProps>;
    RightComponent?: React.ComponentType<ComponentProps>;
    BottomComponent?: React.ComponentType<ComponentProps>;
    ariaLabel?: string;
    ariaRole?: string;
    ariaLive?: string;
    ariaExpanded?: boolean;
    ariaDescribedBy?: string;
    items?: GenericItemItemProps[];
    labelDataTestId?: string;
    skeletonVariant?: 'item' | 'subitem';
  }

  export interface ComponentProps {
    item: GenericItemItemProps;
    ctx: ContextProps;
  }
  export interface GenericItemItemProps {
    dsId?: string;
    type?: string;
    CollapsedComponent?: React.ComponentType<ComponentProps> | null;
    Component?: React.ComponentType<ComponentProps>;
    itemOpts: ItemOptsT;
  }
  export interface OutOfTheBoxMapItemT {
    item: GenericItemItemProps;
  }
  export interface ItemRendererT {
    item: GenericItemItemProps;
    subitems?: JSX.Element[];
    minHeight?: string;
    paddingTop?: string;
    onClick?: () => void;
    shadowStyle?: (opened: boolean, type?: string) => (theme: Theme) => string;
    hasBorderBottom?: boolean;
    borderBottomMr?: string;
    children: React.ReactNode;
  }
}

export const defaultProps: DSLeftNavigationT.DefaultProps = {
  expandedWidth: '240px',
  loading: false,
  expanded: false,
  openedItem: null,
  footerLabel: '',
  onSelectedChange: () => null,
  onFocusChange: () => null,
  onFooterExpand: () => null,
  onFooterClose: () => null,
  items: [],
  labelOverflow: 'wrap' as DSLeftNavigationT.LabelOveflowT,
  onItemClick: () => null,
  isSkeleton: false,
  hideFooterPipe: false,
};

export const DSLeftNavigationPropTypes: DSPropTypesSchema<DSLeftNavigationT.Props> = {
  ...getPropsPerSlotPropTypes(DSLeftNavigationName, LEFT_NAVIGATION_SLOTS),
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,

  expandedWidth: PropTypes.string.description('Width of the navigation when expanded').defaultValue('240px'),
  expanded: PropTypes.bool.description('Whether to show the left navigation expanded or collapsed').defaultValue(false),
  loading: PropTypes.bool.description('Whether the left-nav is loading').defaultValue(false),
  openedItem: PropTypes.string
    .description('The id of the left navigation item you want to be opened')
    .defaultValue(null),
  footerLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
    .description('The label to show in the footer item')
    .defaultValue(''),
  FooterLabelComponent: PropTypes.func.description('The component to show in the footer item'),
  onFooterExpand: PropTypes.func
    .description('The function to call when the left-nav is opened via the footer')
    .defaultValue(() => null),
  onFooterClose: PropTypes.func
    .description('The function to call when the left-nav is closed via the footer')
    .defaultValue(() => null),
  onSelectedChange: PropTypes.func
    .description('A callback which triggers when the selected item changes')
    .defaultValue(() => null),
  onFocusChange: PropTypes.func
    .description('A callback which triggers when the focused item changes')
    .defaultValue(() => null),
  items: PropTypes.arrayOf(PropTypes.object)
    .description('The array of items you want to render inside the left nav')
    .defaultValue([]),
  labelOverflow: PropTypes.oneOf(['wrap', 'wrapAll', 'truncate'])
    .description('How to handle label overflow')
    .defaultValue('wrap'),
  onItemClick: PropTypes.func.description('Callback when an item is clicked').defaultValue(() => null),
  isSkeleton: PropTypes.bool.description('Whether the component is in skeleton mode').defaultValue(false),
  hideFooterPipe: PropTypes.bool.description('Whether to hide the pipe in the footer').defaultValue(false),
  selectedItem: PropTypes.string.description('The currently selected item'),
  selectedParent: PropTypes.string.description('The parent of the currently selected item'),
  disableDefaultSelection: PropTypes.bool
    .description('Whether you do not want to automatically select the first body item')
    .defaultValue(false),
  withoutBodyShadow: PropTypes.bool.description('Whether to hide the body shadow'),
  actionRef: PropTypes.object.description('Ref for controlling focus'),
  HeaderComponent: PropTypes.object.description('Custom header component'),
  BodyHeaderComponent: PropTypes.object.description('Custom body header component'),
};

export const DSLeftNavigationPropTypesSchema =
  DSLeftNavigationPropTypes as unknown as ValidationMap<DSLeftNavigationT.Props>;

export const DSLeftNavigationItemPropTypes: DSPropTypesSchema<DSLeftNavigationT.GenericItemItemProps> = {
  type: PropTypes.oneOf(outOfTheBoxTypes).description('out of the box types'),
  dsId: PropTypes.string.description('unique identifier for the left nav item'),
  Component: PropTypes.func.description(
    "A custom component, receives the item as prop. This is used only when type is not defined or doesn't matches any out-of-the-box",
  ),
  CollapsedComponent: PropTypes.func.description('A component which is renderer when the left-nav is collapsed'),
  itemOpts: PropTypes.object.description('Item options'),
};

export const DSLeftNavigationItemPropTypesSchema =
  DSLeftNavigationItemPropTypes as unknown as ValidationMap<DSLeftNavigationT.GenericItemItemProps>;

const LeftNavItemProps = () => null;
LeftNavItemProps.displayName = 'LeftNavItemProps';
export const LeftNavItemPropsSchema = describe(LeftNavItemProps);
LeftNavItemPropsSchema.propTypes = DSLeftNavigationItemPropTypes;

const LeftNavItemOptions = () => null;
LeftNavItemOptions.displayName = 'LeftNavItemOptions';
export const LeftNavItemOptionsSchema = describe(LeftNavItemOptions);
LeftNavItemOptionsSchema.propTypes = DSLeftNavigationItemPropTypes;
